import {BaseButton, BaseLoading, BasePopup, Icon, SecondaryButton} from "components"
import {StudentStatusLabel} from "components/StudentStatusLabel"
import styles from "./AddManualDailyAttendance.module.css"
import React, {useCallback, useEffect, useRef, useState} from "react"
import moment from "moment"
import {useModel} from "hooks"
import {convertTZ, getFullName, handleError} from "helpers"
import {attendanceService, generalSettingsService, profileService} from "services"
import {Search} from "components/inputs/Search"
import {Auth} from "types/auth"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import {AttendanceEvent, AttendanceEventType, BreakEventType} from "types/attendance"
import cx from "classnames"
import {useDebounce} from "react-use"
import Clock from "./Clock"
import {get} from "lodash"
import {BaseDatePicker, BaseTimePicker} from "components/DateTimePicker"
import {Checkbox, Radio} from "antd"
import {useAttendanceCheckBreakEventSetting} from "hooks/useAttendanceCheckBreakEventSetting"
import {getDisabledEndHours, getDisabledEndMinutes} from "helpers/calendar"
import {Modules, useEdularModulesContext} from "@edular/modules"

type AddManualDailyAttendanceProps = {
    isShow: boolean
    onClose: () => void
    onRefreshData?: () => void
}

export enum DailyAttendanceRegistrationTimeSetting {
    Auto,
    Manual
}

const AddManualDailyAttendance = (props: AddManualDailyAttendanceProps) => {
    const {isShow, onClose} = props
    const [loading, setLoading] = useState(false)
    const model = useModel()
    const [studentSignature, setStudentSignature] = useState<{signatureId: number; imageUrl: string; userId: number}>()
    const [selectedStudent, setSelectedStudent] = useState<Auth.UserProfile>()
    const [search, setSearch] = useState("")
    const [searchDebounce, setSearchDebounce] = useState("")
    const [dateTime, setDateTime] = useState(moment().toISOString())
    const oldDateTime = useRef<any>()
    const [dailyAttendanceRegistrationTimeSetting, setDailyAttendanceRegistrationTimeSetting] = useState(
        DailyAttendanceRegistrationTimeSetting.Auto
    )
    const {isModuleEnable} = useEdularModulesContext()

    const [listStudents, setListStudents] = useState<Auth.UserProfile[]>([])
    const [events, setEvents] = useState<AttendanceEvent[]>([])
    const dateFormat = model.getUserDateFormat()
    const timeFormat = model.getUserTimeFormat()
    const [isLunch, setIsLunch] = useState(false)
    const {isBreakEnabled} = useAttendanceCheckBreakEventSetting()
    let typeButton: AttendanceEventType = AttendanceEventType.Checkin
    const latestEvent = events.length ? get(events, [events.length - 1]) : undefined
    if (latestEvent && latestEvent.eventType === AttendanceEventType.BreakStart) {
        typeButton = AttendanceEventType.BreakEnd
    } else if (
        latestEvent &&
        [AttendanceEventType.Checkin, AttendanceEventType.BreakEnd].includes(latestEvent.eventType)
    ) {
        typeButton = AttendanceEventType.BreakStart
    }

    const latestEventCreatedAt = latestEvent?.createdAt

    useDebounce(() => setSearchDebounce(search), 400, [search])

    const renderButtonInOut = (eventType: AttendanceEventType) => {
        if (!isBreakEnabled && [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(typeButton)) {
            return null
        }
        return (
            <div className={styles.groupButtonInOut}>
                <div onClick={(e) => onPressInOut(e, eventType)} className={styles.buttonOut}>
                    {[AttendanceEventType.Checkin, AttendanceEventType.BreakEnd].includes(typeButton) ? (
                        <div className={styles.actionButton} style={{backgroundColor: "#18A957"}}>
                            <Icon icon="IN_CLASS" color="white" />
                        </div>
                    ) : (
                        <div className={styles.actionButton} style={{backgroundColor: "#DF1642"}}>
                            <Icon icon="OUT_CLASS" color="white" />
                        </div>
                    )}
                </div>

                {isModuleEnable(Modules.AcademicLocationTrackingAttendance) &&
                isModuleEnable(Modules.AcademicUseGeoFencing) &&
                [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(eventType) ? (
                    <Checkbox
                        checked={isLunch}
                        disabled={latestEvent?.eventType === AttendanceEventType.BreakStart}
                        onChange={(e) => {
                            setIsLunch(!isLunch)
                        }}>
                        <span className={styles.infoLabel}>Is Lunch</span>
                    </Checkbox>
                ) : null}
            </div>
        )
    }

    useEffect(() => {
        if (selectedStudent && dailyAttendanceRegistrationTimeSetting === DailyAttendanceRegistrationTimeSetting.Auto) {
            setDateTime(moment.tz(moment().toISOString(), selectedStudent.timezone).format("YYYY-MM-DD"))
        }
    }, [selectedStudent, dailyAttendanceRegistrationTimeSetting])

    const onPressInOut = (e: React.MouseEvent, eventType: AttendanceEventType) => {
        e.stopPropagation()

        createDailyAttendance(eventType)
    }

    const renderEvents = () => {
        return (
            <div className={styles.eventsContainer}>
                {events?.map((event, index) => {
                    return (
                        <>
                            <div className={styles.event} key={index}>
                                {event.eventType === AttendanceEventType.Checkin ? (
                                    <Icon icon="CHECKIN_FILL" className={styles.eventIcon} color={"#18a957"} />
                                ) : null}
                                {event.eventType === AttendanceEventType.Checkout ? (
                                    <Icon icon="CHECKOUT_FILL" className={styles.eventIcon} color={"#DF1642"} />
                                ) : null}
                                {event.eventType === AttendanceEventType.BreakEnd ? (
                                    <Icon icon="ATTENDANCE_IN" className={styles.eventIcon} color={"#18a957"} />
                                ) : null}
                                {event.eventType === AttendanceEventType.BreakStart ? (
                                    <Icon icon="ATTENDANCE_OUT" className={styles.eventIcon} color={"#DF1642"} />
                                ) : null}
                                <span className={styles.eventTime}>
                                    {moment(event.createdAt).format(model.getUserTimeFormat())}
                                </span>
                            </div>
                            {index !== events?.length - 1 ? (
                                <div className={styles.eventSeparator} key={index} />
                            ) : null}
                        </>
                    )
                })}
            </div>
        )
    }

    const resetState = () => {
        oldDateTime.current = undefined
        setEvents([])
        setStudentSignature(undefined)
        setSelectedStudent(undefined)
        setListStudents([])
        setSearch("")
    }

    const onCancel = useCallback(() => {
        if (selectedStudent) {
            resetState()
        } else {
            resetState()
            onClose()
        }
    }, [selectedStudent, onClose])

    const getListStudent = useCallback(async () => {
        if (searchDebounce === "") {
            setListStudents([])
            return
        }
        try {
            let {data} = await profileService.getAll({
                linkedObjects: true,
                filter: {
                    type: ["student"],
                    search: searchDebounce,
                    state: [
                        Auth.UserProfileState.Student,
                        Auth.UserProfileState.Alumni,
                        Auth.UserProfileState.TemporaryOut
                    ],
                    campuses: [],
                    excludeInactiveProfiles: true,
                    allowsDailyAttendanceManagement: true
                },
                range: {
                    limit: UNLIMITED_PAGE_SIZE,
                    offset: 0
                }
            })
            data = data.map((student) => ({
                ...student,
                photo:
                    typeof student.photo === "string" && student.photo?.length
                        ? JSON.parse(student.photo)
                        : student.photo,
                profileId: student.id
            }))

            setListStudents(data)
        } catch (e) {
            handleError(e)
        }
    }, [searchDebounce])

    useEffect(() => {
        getListStudent()
    }, [getListStudent])

    const getAttendanceDetail = useCallback(
        async (forceRefresh = false) => {
            const oldDate = oldDateTime.current ? moment(oldDateTime.current).format("YYYY-MM-DD") : null
            const newDate = moment(dateTime).format("YYYY-MM-DD")

            if (oldDate !== newDate || forceRefresh) {
                setLoading(true)
                oldDateTime.current = moment(dateTime).toISOString()

                try {
                    const {data, total} = await attendanceService.getDailyAttendanceList({
                        range: {
                            page: 1,
                            pageSize: UNLIMITED_PAGE_SIZE
                        },
                        studentProfileIds: [selectedStudent.profileId],
                        fromDate: moment(dateTime).format("YYYY-MM-DD"),
                        toDate: moment(dateTime).format("YYYY-MM-DD")
                    })
                    const events = (data || [])
                        .reduce((events, attendance) => {
                            return [...events, ...(attendance.events ?? [])]
                        }, [])
                        .map((event) => {
                            const createdAt = moment(convertTZ(event.createdAt, selectedStudent.timezone)).toISOString()
                            return {
                                ...event,
                                createdAt
                            }
                        })
                        .sort(function (left, right) {
                            return moment.utc(left.createdAt).diff(moment.utc(right.createdAt))
                        })
                    const latestEvent = events.length ? get(events, [events.length - 1]) : undefined

                    if (
                        latestEvent?.eventType === AttendanceEventType.BreakStart &&
                        latestEvent.breakEventType === BreakEventType.Lunch
                    ) {
                        setIsLunch(true)
                    }
                    setEvents(events)
                } catch (err) {
                    handleError(err)
                } finally {
                    setLoading(false)
                }
            }
        },
        [selectedStudent, dateTime]
    )

    useEffect(() => {
        if (selectedStudent) {
            getAttendanceDetail(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateTime, selectedStudent])

    const createDailyAttendance = useCallback(
        async (eventType: AttendanceEventType) => {
            if (selectedStudent.currentMajorVersionId) {
                setLoading(true)
                try {
                    const params = {
                        majorVersionId: selectedStudent.currentMajorVersionId,
                        eventType: eventType,
                        profileId: selectedStudent.profileId,
                        campusId: selectedStudent?.campuses[0].id,
                        message: "Manual create daily attendance",
                        breakEventType: [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(
                            eventType
                        )
                            ? isLunch
                                ? BreakEventType.Lunch
                                : BreakEventType.Break
                            : null,
                        createdAt:
                            dailyAttendanceRegistrationTimeSetting === DailyAttendanceRegistrationTimeSetting.Auto
                                ? undefined
                                : moment(dateTime).tz(selectedStudent.timezone, true).toISOString()
                    }

                    await attendanceService.createDailyAttendance(params)
                    await getAttendanceDetail(true)
                    if (eventType === AttendanceEventType.BreakStart && isLunch) {
                        setIsLunch(true)
                    } else {
                        setIsLunch(false)
                    }
                } catch (err) {
                    handleError(err)
                } finally {
                    setLoading(false)
                }
            }
        },
        [getAttendanceDetail, selectedStudent, dailyAttendanceRegistrationTimeSetting, dateTime, isLunch]
    )

    const renderUserInfo = useCallback(
        (user: Auth.UserProfile & {customProfileId?: number}, isStudent: boolean = false) => {
            let photo = "/image/DefaultAvatar.png"
            if (user?.photo) {
                photo = user.photo["256"]
            }
            const checkInEvent = events?.find((event) => event.eventType === AttendanceEventType.Checkin)

            return (
                <>
                    <p className={styles.label} style={{marginTop: 40}}>
                        {isStudent ? "STUDENT" : "INSTRUCTOR"}
                    </p>
                    <div className={styles.instructorBox}>
                        <div className={styles.avatar}>
                            <img src={photo} alt="instructorAvt" />
                        </div>
                        <div className={styles.instructorDetail}>
                            <div className={styles.nameBox} style={{marginTop: 0}}>
                                <span className={styles.instructorName}>{getFullName(user)}</span>
                                <div className={styles.checkInBox}>
                                    {!!checkInEvent && isStudent && (
                                        <>
                                            <Icon icon="CHECKIN_FILL" className={styles.eventIcon} color={"#18a957"} />
                                            <span className={styles.eventTime}>Checkin: </span>
                                            <span className={styles.eventTime}>
                                                {moment(checkInEvent.createdAt).format(model.getUserTimeFormat())}
                                            </span>
                                            <div className={styles.statusWrap}>
                                                <div className={cx(styles.signatureWrap, styles.signature__approval)}>
                                                    <img
                                                        src={studentSignature?.imageUrl}
                                                        alt="signature"
                                                        className={styles.signature__img}
                                                    />
                                                </div>
                                                <div className={styles.dateInfo}>
                                                    <span className={styles.dateInfo__text}>
                                                        {moment().local(true).format(model.getUserDateTimeFormat())}
                                                    </span>
                                                    <span className={styles.dateInfo__text}>{getFullName(user)}</span>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div
                                className={cx(styles.instructorInfo, {
                                    [styles.line]: isStudent
                                })}>
                                <div className={styles.nameBox} style={!isStudent ? {flex: "none"} : {}}>
                                    <span className={styles.labelUserId}>User ID</span>
                                    <span className={styles.valueUserId}>{user?.customUserId}</span>
                                </div>
                                {isStudent ? (
                                    <div className={styles.nameBox}>
                                        <span className={styles.labelUserId}>Enrollment ID:</span>
                                        <span className={styles.valueUserId}>{user?.customProfileId}</span>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </>
            )
        },
        [events, model, studentSignature]
    )

    const renderStudentAvatar = (student) => {
        let studentPhoto = "/image/DefaultAvatar.png"
        if (student?.photo) {
            studentPhoto = student.photo["64"]
        }
        return <img src={studentPhoto} className={styles.studentAvatar} alt="" />
    }

    const onSelectStudent = useCallback((student) => {
        setSelectedStudent({...student, currentMajorVersionId: student.currentMajorVersionId?.id})
    }, [])

    const renderStudent = useCallback(
        (student) => {
            return (
                <div key={student.id} onClick={() => onSelectStudent(student)} className={styles.studentItem}>
                    <div className={styles.studentDetail}>
                        {renderStudentAvatar(student)}
                        <span className={styles.studentName}>{student.customProfileId}</span>
                        <span className={styles.studentName}>{getFullName(student)}</span>
                        {student.status ? <StudentStatusLabel status={student.status} /> : null}
                    </div>
                    <Icon icon="ARROW_CIRCLE_RIGHT" className={styles.selectStudentIcon} />
                </div>
            )
        },
        [onSelectStudent]
    )

    return (
        <>
            <BasePopup
                isShow={isShow}
                onClose={() => {
                    resetState()
                    onClose()
                }}
                width="90vw"
                leftIcon="PERSON_ADD">
                <div className={styles.wrap}>
                    <p className={styles.title}>Add daily attendance</p>
                    {selectedStudent ? (
                        <div className={styles.header}>
                            <div className={styles.date}>
                                <div className={styles.manualTime}>
                                    <Radio.Group
                                        value={dailyAttendanceRegistrationTimeSetting}
                                        onChange={(event) => {
                                            setDailyAttendanceRegistrationTimeSetting(event.target.value)
                                            if (event.target.value === DailyAttendanceRegistrationTimeSetting.Auto) {
                                                setDateTime(
                                                    moment
                                                        .tz(moment().toISOString(), selectedStudent.timezone)
                                                        .format("YYYY-MM-DD")
                                                )
                                            } else {
                                                if (latestEventCreatedAt) {
                                                    setDateTime(latestEventCreatedAt)
                                                } else {
                                                    setDateTime(
                                                        moment
                                                            .tz(moment().toISOString(), selectedStudent.timezone)
                                                            .toISOString()
                                                    )
                                                }
                                            }
                                        }}>
                                        <Radio
                                            value={DailyAttendanceRegistrationTimeSetting.Auto}
                                            className={styles.timeChoice}>
                                            Auto
                                        </Radio>
                                        <Radio
                                            value={DailyAttendanceRegistrationTimeSetting.Manual}
                                            className={styles.timeChoice}>
                                            Manual
                                        </Radio>
                                    </Radio.Group>

                                    {dailyAttendanceRegistrationTimeSetting ===
                                    DailyAttendanceRegistrationTimeSetting.Auto ? (
                                        <p className={styles.textDate}>
                                            {selectedStudent.timezone
                                                ? moment()
                                                      .tz(selectedStudent.timezone)
                                                      .format(model.getUserDateFormat())
                                                : moment().format(model.getUserDateFormat())}
                                        </p>
                                    ) : (
                                        <BaseDatePicker
                                            className={styles.datePicker}
                                            placeholder={dateFormat}
                                            format={dateFormat}
                                            value={dateTime ? moment(dateTime) : undefined}
                                            onChange={(newValue) => {
                                                const date = moment(newValue).clone()

                                                if (latestEventCreatedAt) {
                                                    date.set({
                                                        hours: moment(latestEventCreatedAt).get("hours"),
                                                        minutes: moment(latestEventCreatedAt).get("minutes"),
                                                        seconds: 0,
                                                        milliseconds: 0
                                                    })
                                                }

                                                setDateTime(date.toISOString())
                                            }}
                                        />
                                    )}

                                    {dailyAttendanceRegistrationTimeSetting ===
                                    DailyAttendanceRegistrationTimeSetting.Auto ? (
                                        <Clock timezone={selectedStudent.timezone} />
                                    ) : (
                                        <BaseTimePicker
                                            value={dateTime ? moment(dateTime) : undefined}
                                            placeholder="Time"
                                            format={timeFormat}
                                            className={styles.timePicker}
                                            disabledHours={() => getDisabledEndHours(latestEventCreatedAt)}
                                            disabledMinutes={() => {
                                                const dateTimeHours = moment(dateTime).clone().get("hours")
                                                const latestEventCreatedHours = moment(latestEventCreatedAt)
                                                    .clone()
                                                    .get("hours")

                                                if (dateTimeHours > latestEventCreatedHours) {
                                                    return []
                                                }

                                                return getDisabledEndMinutes(latestEventCreatedAt, latestEventCreatedAt)
                                            }}
                                            onChange={(newValue) => {
                                                setDateTime(newValue)
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {!selectedStudent ? (
                        <>
                            <div className={styles.student}>
                                <span className={styles.label}>STUDENT</span>
                                <span className={styles.subLabel}>Search for a student</span>
                            </div>
                            <div className={styles.searchBar}>
                                <Search value={search} placeholder={"Search"} onChange={setSearch} />
                            </div>

                            <div className={styles.searchBar} style={{marginTop: 40, marginBottom: 8}}>
                                <span className={styles.totalStudent} style={{color: "#1e90ff", marginRight: "10px"}}>
                                    {listStudents.length}
                                </span>
                                <span className={styles.totalStudent}>STUDENTS</span>
                            </div>

                            <div className={styles.listStudent}>{listStudents.map(renderStudent)}</div>
                        </>
                    ) : (
                        <>
                            {renderUserInfo(selectedStudent, true)}
                            <div className={styles.checkContainer}>
                                <span className={styles.label}>CHECKS</span>
                            </div>
                            <div className={styles.eventContainer}>
                                {!events.length ? (
                                    <span className={styles.labelCheckLesson}>Check daily attendance</span>
                                ) : (
                                    renderEvents()
                                )}
                                <div className={styles.eventAction}>
                                    {renderButtonInOut(typeButton)}
                                    {[AttendanceEventType.Checkin, AttendanceEventType.BreakEnd].includes(
                                        latestEvent?.eventType
                                    ) ? (
                                        <BaseButton
                                            title="Check out"
                                            variant="secondary"
                                            loading={loading}
                                            className={styles.cancelButton}
                                            onClick={() => {
                                                createDailyAttendance(AttendanceEventType.Checkout)
                                            }}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        </>
                    )}
                    <div className={styles.buttonSubmit}>
                        <SecondaryButton title={"CANCEL"} onClick={onCancel} />
                        <div />
                    </div>
                </div>
                <BaseLoading isShow={loading} />
            </BasePopup>
        </>
    )
}

export default AddManualDailyAttendance
